import React, { useState, useEffect } from 'react';
import { TimeRange } from '../../types/analytics';

interface DateRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}

type PresetOption = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'custom';

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
  const [preset, setPreset] = useState<PresetOption>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate date ranges based on preset
  const getDateRangeForPreset = (presetType: PresetOption): TimeRange => {
    const today = new Date();
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (presetType) {
      case 'today': {
        const dateStr = formatDate(today);
        return { startDate: dateStr, endDate: dateStr, preset: 'today' };
      }

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const dateStr = formatDate(yesterday);
        return { startDate: dateStr, endDate: dateStr, preset: 'yesterday' };
      }

      case 'last7days': {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        return {
          startDate: formatDate(sevenDaysAgo),
          endDate: formatDate(today),
          preset: 'last7days',
        };
      }

      case 'last30days': {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
        return {
          startDate: formatDate(thirtyDaysAgo),
          endDate: formatDate(today),
          preset: 'last30days',
        };
      }

      case 'thisMonth': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          startDate: formatDate(firstDay),
          endDate: formatDate(today),
          preset: 'thisMonth',
        };
      }

      case 'custom':
        return {
          startDate: customStartDate,
          endDate: customEndDate,
          preset: 'custom',
        };

      default:
        return { startDate: formatDate(today), endDate: formatDate(today), preset: 'today' };
    }
  };

  // Handle preset button click
  const handlePresetClick = (presetType: PresetOption) => {
    setPreset(presetType);
    // Don't call onChange for custom preset until user enters dates
    if (presetType !== 'custom') {
      const range = getDateRangeForPreset(presetType);
      onChange(range);
    }
  };

  // Handle custom date change
  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      onChange({
        startDate: customStartDate,
        endDate: customEndDate,
        preset: 'custom',
      });
    }
  };

  // Initialize with current value or default to today
  useEffect(() => {
    if (!value.startDate && !value.endDate) {
      handlePresetClick('today');
    } else if (value.preset) {
      setPreset(value.preset);
      if (value.preset === 'custom') {
        setCustomStartDate(value.startDate || '');
        setCustomEndDate(value.endDate || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update custom dates when they change
  useEffect(() => {
    if (preset === 'custom') {
      handleCustomDateChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStartDate, customEndDate]);

  const presetButtons: Array<{ value: PresetOption; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'custom', label: 'Custom Range' },
  ];

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-sm font-medium text-gray-300">Time Range</span>
      </div>

      {/* Preset Buttons */}
      <div className="grid grid-cols-3 gap-2">
        {presetButtons.map((button) => (
          <button
            key={button.value}
            onClick={() => handlePresetClick(button.value)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              preset === button.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {button.label}
          </button>
        ))}
      </div>

      {/* Custom Date Inputs */}
      {preset === 'custom' && (
        <div className="space-y-2 pt-2 border-t border-gray-700">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              min={customStartDate}
              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Current Range Display */}
      {value.startDate && value.endDate && (
        <div className="pt-2 border-t border-gray-700">
          <div className="text-xs text-gray-400">
            Showing data from{' '}
            <span className="text-gray-200 font-medium">{value.startDate}</span> to{' '}
            <span className="text-gray-200 font-medium">{value.endDate}</span>
            {value.startDate === value.endDate && (
              <span className="text-gray-500"> (single day)</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
